import { Stack } from '@mui/material';
import {
  Assessment,
  AssessmentBase,
  AssessmentForReport,
  DATE_FORMAT_SHORT_FNS,
} from '@schooly/api';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import {
  GridRowCell,
  GridRowDate,
  GridRowName,
  ModalContent,
  RowSelectable,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { FC, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ModalEmptyMainArea } from '../../../components/uikit-components/Modal/ModalEmptyMainArea';
import { ModalPeopleExtensionPanel } from '../../../components/uikit-components/Modal/ModalPeopleExtensionPanel';
import { ReportForm } from '../../../context/report/WithReportEditContext';
import useAppLocation from '../../../hooks/useAppLocation';
import { SelectAssessmentExternalSidebar } from './SelectAssessmentExternalSidebar/SelectAssessmentExternalSidebar';

type ReportCreateModalAdditionalAssessmentsProps = {
  active: boolean;
  errorId?: string;
  onAddClick?: () => void;
  form: UseFormReturn<ReportForm>;
};

export const ReportCreateModalAdditionalAssessments: FC<
  ReportCreateModalAdditionalAssessmentsProps
> = ({ form, errorId, onAddClick, active }) => {
  const { $t } = useIntl();

  const additionalAssessments = form.watch('assessments');

  const onAssessmentSelect = useCallback(
    (v: Assessment | AssessmentForReport) => {
      form.setValue(
        'assessments',
        additionalAssessments.map((a) => a.id).includes(v.id)
          ? additionalAssessments.filter((a) => a.id !== v.id)
          : [...additionalAssessments, v],
      );
    },
    [additionalAssessments, form],
  );

  const assessmentsFilter = useCallback((v: AssessmentBase) => {
    if (v.autogenerated) return false;

    return true;
  }, []);

  return (
    <>
      <ModalPeopleExtensionPanel
        titleId="reports-AdditionalAssessments"
        addActionId="reports-AddAssessments"
        editActionId="reports-EditAssessments"
        count={additionalAssessments.length}
        onAddClick={onAddClick}
        errorId={errorId}
        active={active}
        sidebarContent={
          <SelectAssessmentExternalSidebar
            assessmentListFilter={assessmentsFilter}
            selectedIds={additionalAssessments.map((a) => a.id)}
            onCardClick={onAssessmentSelect}
          />
        }
        testId="report-add-assessment-button"
      >
        <ModalContent active flat sx={{ pt: 0 }}>
          {!additionalAssessments?.length ? (
            <ModalEmptyMainArea label={$t({ id: 'reports-Assessments' }).toLowerCase()} />
          ) : (
            <Stack sx={{ overflowY: 'auto', px: 2.5 }}>
              {additionalAssessments?.map((assessment) => (
                <AssessmentRow
                  key={assessment.id}
                  assessment={assessment}
                  onClick={onAssessmentSelect}
                />
              ))}
            </Stack>
          )}
        </ModalContent>
      </ModalPeopleExtensionPanel>
    </>
  );
};

type AssessmentRowProps = {
  assessment: Assessment | AssessmentForReport;
  onClick: (assessment: Assessment | AssessmentForReport) => void;
};

const AssessmentRow = ({ assessment, onClick }: AssessmentRowProps) => {
  const location = useAppLocation();

  const { getAssessmentEvaluationTitle } = useAssessmentLabels();
  const evaluationTitle = getAssessmentEvaluationTitle(assessment);

  return (
    <RowSelectable
      selected
      onClick={() => onClick(assessment)}
      sx={{ '& .GridRowItem': { gap: 2, py: 1.5 } }}
    >
      <GridRowDate
        sx={(theme) => ({
          flex: '0 0 45px',
          '.GridRowItem:hover &': { color: theme.palette.common.grey2 },
        })}
      >
        {format(newDateTimezoneOffset(assessment.assessment_date), DATE_FORMAT_SHORT_FNS)}
      </GridRowDate>
      <GridRowName sx={{ flex: '1 1 50%', overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center">
          <TypographyWithOverflowHint
            variant="h3"
            component={Link}
            to={`/assessments/${assessment.id}`}
            state={{
              prevPathname: `${location.pathname}${location.hash ?? ''}`,
              backgroundLocation: location,
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
            sx={{ '&:hover': { textDecoration: 'underline' } }}
          >
            {assessment.name}
          </TypographyWithOverflowHint>
        </Stack>
      </GridRowName>
      <GridRowCell variant="body1" sx={{ flex: '1 1 50%', overflow: 'hidden' }}>
        <TypographyWithOverflowHint
          sx={(theme) => ({
            color: evaluationTitle ? theme.palette.common.grey2 : theme.palette.common.grey,
            '.GridRowItem:hover &': {
              color: evaluationTitle ? theme.palette.primary.main : theme.palette.common.grey2,
            },
          })}
        >
          {evaluationTitle ?? '-'}
        </TypographyWithOverflowHint>
      </GridRowCell>
    </RowSelectable>
  );
};
